import React from 'react';
import { CheckCircle, Building2, Sparkles, Shield, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServiceHero from '../components/ServiceHero';
import FloatingQuoteButton from '../components/FloatingQuoteButton';

const CommercialCleaning = () => {
  const scrollToQuote = () => {
    const element = document.querySelector('#quote-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const windowServices = [
    "Interior & exterior window cleaning",
    "Multi-story and ladder access cleaning",
    "Storefront glass maintenance",
    "Hard water stain removal",
    "Post-construction window cleaning",
    "Scheduled maintenance programs"
  ];

  const powerWashSurfaces = [
    "Building facades",
    "Storefronts",
    "Sidewalks & entryways",
    "Parking lots & garages",
    "Loading docks",
    "Dumpster pads",
    "Exterior signage",
    "Stucco, brick, vinyl, and composite surfaces"
  ];

  const whyChooseUs = [
    { icon: Shield, text: "Fully insured & professional crew" },
    { icon: Clock, text: "Flexible scheduling (including early morning/off-hours service)" },
    { icon: Building2, text: "Routine maintenance plans available" },
    { icon: Sparkles, text: "Clear communication & dependable timelines" },
    { icon: CheckCircle, text: "100% satisfaction guarantee" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <ServiceHero
        title="Commercial Cleaning"
        headline="Dirty Building?"
        description="Professional Presentation. Reliable Service. Lasting Impressions. Your building reflects your business."
        backgroundImage="https://customer-assets.emergentagent.com/job_034ba03a-ab9b-460a-8d65-ab113159d799/artifacts/r5x6weih_ChatGPT%20Image%20Feb%2027%2C%202026%20at%2012_58_41%20AM.png"
        defaultService="Commercial Cleaning"
      />

      {/* Intro Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Your building reflects your business. Clean glass, bright facades, and well-maintained
            exteriors send a clear message — professionalism, pride, and attention to detail.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            At <span className="text-gradient-reflections font-bold">Reflections Cleanings</span>,
            we provide commercial window cleaning and power washing services throughout the Hamptons
            and Long Island. We work with property managers, retail storefronts, office buildings,
            restaurants, medical facilities, and commercial properties that require consistent,
            high-level exterior maintenance.
          </p>
        </div>
      </section>

      {/* Commercial Window Cleaning Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-6" data-testid="commercial-window-heading">
                Commercial Window Cleaning
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Crystal-clear windows improve natural light, visibility, and curb appeal while
                maintaining a polished, professional appearance.
              </p>
              <p className="text-gray-700 font-medium mb-4">
                Our commercial window services include:
              </p>
              <ul className="space-y-3 mb-8">
                {windowServices.map((service, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{service}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 italic">
                We operate efficiently and discreetly, minimizing disruption to staff and customers
                while delivering flawless results.
              </p>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_034ba03a-ab9b-460a-8d65-ab113159d799/artifacts/r5x6weih_ChatGPT%20Image%20Feb%2027%2C%202026%20at%2012_58_41%20AM.png"
                alt="Commercial window cleaning service"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-4 -left-4 bg-blue-600 text-white px-6 py-4 rounded-2xl shadow-lg">
                <div className="flex items-center gap-3">
                  <Building2 className="w-8 h-8" />
                  <div>
                    <div className="text-xl font-bold">Commercial</div>
                    <div className="text-sm text-blue-100">Grade Service</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Power Washing Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="order-2 lg:order-1 relative">
              <img
                src="https://customer-assets.emergentagent.com/job_034ba03a-ab9b-460a-8d65-ab113159d799/artifacts/2f0ayzmh_ChatGPT%20Image%20Feb%2026%2C%202026%20at%2004_39_45%20PM.png"
                alt="Commercial power washing service"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute -top-4 -right-4 bg-white text-gray-900 px-6 py-4 rounded-2xl shadow-lg border border-gray-100">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-blue-600" />
                  <div>
                    <div className="text-lg font-bold text-blue-600">Soft Wash</div>
                    <div className="text-sm text-gray-600">Safe for all surfaces</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-6" data-testid="commercial-power-heading">
                Commercial Power Washing
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                A clean exterior protects your property and reinforces your brand image.
                We safely clean:
              </p>
              <ul className="space-y-3 mb-8">
                {powerWashSurfaces.map((surface, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{surface}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 italic">
                Using professional-grade equipment and soft wash systems where appropriate,
                we remove dirt, salt, mold, mildew, algae, and environmental buildup without
                damaging surfaces.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Businesses Choose Us */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 text-center" data-testid="why-choose-heading">
            Why Businesses Choose <span className="text-gradient-reflections">Reflections Cleanings</span>
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            We understand that commercial properties require consistency, safety compliance,
            and reliable service partners. Our team shows up on time, prepared, and ready to
            maintain your property to the highest standard.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow flex items-start gap-4">
                <div className="bg-blue-100 p-3 rounded-xl flex-shrink-0">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-gray-700 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Maintenance Programs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="maintenance-heading">
            Maintenance Programs Available
          </h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Ask about custom recurring service plans designed to keep your building looking its
            best year-round — ideal for retail centers, offices, HOAs, and multi-unit properties.
          </p>
          <button
            onClick={scrollToQuote}
            className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-colors"
            data-testid="maintenance-cta"
          >
            Ask About Maintenance Plans
          </button>
        </div>
      </section>

      {/* Quote Section */}
      <section id="quote-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="commercial-quote-heading">
            Request a Commercial Quote
          </h2>
          <p className="text-lg text-gray-600 mb-2">
            Partner with a team that understands presentation matters.
          </p>
          <p className="text-gray-600 mb-8">
            Request a consultation today and receive a detailed quote within 1 business day.
          </p>
          <Link
            to="/#quote"
            data-testid="commercial-quote-link"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Get Your Free Commercial Quote
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingQuoteButton />
    </div>
  );
};

export default CommercialCleaning;
